import { inject, injectable } from "inversify";
import {
  CommentModel,
  CommentThreadRecipientModel,
  ICPLCommentsClient,
  NewCommentRecipientModel,
} from "./api/cplapi/CPLAPI";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { CommentUpdateModel } from "./classes/CommentUpdateModel";
import { NewCommentModel } from "./classes/NewCommentModel";
import { ExternalAccessCommentModel } from "./classes/ExternalAccessCommentModel";

export interface ICommentsService {
  getComments(commentThreadID: string, signal?: AbortSignal): Promise<CommentModel[]>;
  getCommentThreadRecipients(commentThreadID: string): Promise<CommentThreadRecipientModel[]>;
  createSystemComment(newCommentModel: NewCommentModel): Promise<number>;
  createComment(newCommentModel: NewCommentModel): Promise<number>;
  createExternalAccessComment(externalAccessCommentModel: ExternalAccessCommentModel): Promise<number>;
  updateComment(commentUpdateModel: CommentUpdateModel): Promise<void>;
  makeCommentPublic(commentID: number): Promise<void>;
  deleteComment(commentID: number): Promise<void>;
  downloadCommentAttachment(commentAttachmentID: number): Promise<File>;
  deleteRecipient(recipientID: number): Promise<void>;
  addRecipient(newCommentRecipientModel: NewCommentRecipientModel): Promise<void>;
  updateCommentReadStatus(commentID: number, commentThreadID: string): Promise<void>;
}

@injectable()
export class CommentsService implements ICommentsService {
  @inject(SERVICE_TYPES.CPLCommentsClient) cplCommentClient!: ICPLCommentsClient;

  async getComments(commentThreadID: string, signal?: AbortSignal): Promise<CommentModel[]> {
    return this.cplCommentClient.getComments(commentThreadID, signal);
  }

  async getCommentThreadRecipients(commentThreadID: string): Promise<CommentThreadRecipientModel[]> {
    return this.cplCommentClient.getCommentThreadRecipients(commentThreadID);
  }

  async createSystemComment(newCommentModel: NewCommentModel): Promise<number> {
    return this.cplCommentClient.createSystemComment(newCommentModel);
  }

  async createComment(newCommentModel: NewCommentModel): Promise<number> {
    return this.cplCommentClient.createComment(newCommentModel);
  }

  async createExternalAccessComment(externalAccessCommentModel: ExternalAccessCommentModel): Promise<number> {
    return this.cplCommentClient.createExternalAccessComment(externalAccessCommentModel);
  }

  async updateComment(commentUpdateModel: CommentUpdateModel): Promise<void> {
    return this.cplCommentClient.updateComment(commentUpdateModel);
  }

  async makeCommentPublic(commentID: number): Promise<void> {
    return this.cplCommentClient.makeCommentPublic(commentID);
  }

  async deleteComment(commentID: number): Promise<void> {
    return this.cplCommentClient.deleteComment(commentID);
  }

  async deleteRecipient(recipientID: number): Promise<void> {
    return this.cplCommentClient.deleteRecipient(recipientID);
  }

  async addRecipient(newCommentRecipientModel: NewCommentRecipientModel): Promise<void> {
    return this.cplCommentClient.addRecipient(newCommentRecipientModel);
  }

  async downloadCommentAttachment(commentAttachmentID: number): Promise<File> {
    const fileResponse = await this.cplCommentClient.downloadCommentAttachment(commentAttachmentID);
    return new File([fileResponse.data], fileResponse.fileName!);
  }

  async updateCommentReadStatus(commentID: number, commentThreadID: string): Promise<void> {
    return this.cplCommentClient.updateCommentReadStatus(commentID, commentThreadID);
  }
}
